/**
 * Formato del archivo de respaldo y su validación (design.md, sección 10).
 *
 * Este módulo es CÓDIGO PURO: no importa Dexie ni toca la base de datos. Está
 * separado de backup.ts para probarlo sin navegador, porque es la pieza que
 * decide si los datos del teléfono se reemplazan o no. Un error aquí destruye
 * información.
 *
 * Origen: Control de Negocio de Comida. Cambios: perfil en la cabecera,
 * validación de registros en memoria, reglas de referencia de DuoFit y
 * confirmación reforzada en lugar de bloqueo.
 */

import { calcularChecksum, checksumCoincide } from './checksum';
import { normalizarAjustes } from './ajustes';
import { ErrorRespaldo } from '@/domain/errors';
import { esFechaLocal, minima, maxima, type FechaLocal } from '@/domain/dates';
import {
  TABLAS,
  ETIQUETAS_TABLA,
  VERSION_ESQUEMA,
  escribirTabla,
  leerTabla,
  type MapaTablas,
  type NombreTabla,
} from './types';

/** Identificador fijo de la app en los archivos. El nombre visible puede cambiar; este no. */
export const IDENTIFICADOR_APP = 'app-gym-salud';

/**
 * Versión del FORMATO del archivo, distinta de la versión del esquema.
 * El formato describe la envoltura; el esquema, el contenido de las tablas
 * (design.md, 10.8). El formato 2 es la envoltura cifrada (backup-crypto.ts).
 */
export const FORMATO_VERSION = 1;
export const FORMATO_CIFRADO = 2;

/**
 * Esquema en que apareció cada tabla. Un respaldo anterior no la trae, y eso
 * no es un aviso: es lo esperado. Sin esta lista, restaurar un respaldo de la
 * Fase 0 avisaría de "Fotos de progreso" como si faltara algo.
 */
export const TABLA_DESDE_ESQUEMA: Readonly<Partial<Record<NombreTabla, number>>> = { photos: 3 };

/** Mensaje de la versión vieja ante un archivo que no conoce (Req. 6.16). */
export const MENSAJE_VERSION_NUEVA =
  'Este respaldo es de una versión más nueva de DuoFit; actualiza la app. ' +
  'No se modificó ningún dato.';

export interface PerfilRespaldo {
  id: string | null;
  nombre: string | null;
}

export interface ArchivoRespaldo {
  app: string;
  formatVersion: number;
  schemaVersion: number;
  exportedAt: string;
  /** 'standalone' si se exportó desde la app instalada, 'navegador' si no. */
  device: string;
  /** De los ajustes; sirve para la advertencia de perfil distinto (Req. 6.15). */
  perfil: PerfilRespaldo;
  counts: Record<string, number>;
  dateRange: { from: FechaLocal; to: FechaLocal } | null;
  /** "sha256:<hex>", solo sobre `data`, con serialización determinista. */
  checksum: string;
  data: MapaTablas;
}

export interface ConteoTabla {
  tabla: NombreTabla;
  etiqueta: string;
  registros: number;
}

export interface ResumenRespaldo {
  formatVersion: number;
  schemaVersion: number;
  exportedAt: string;
  device: string;
  perfil: PerfilRespaldo;
  conteos: ConteoTabla[];
  /** Sin contar los ajustes. */
  totalRegistros: number;
  rangoFechas: { desde: FechaLocal; hasta: FechaLocal } | null;
  traeChecksum: boolean;
  checksumValido: boolean;
  requiereMigracion: boolean;
  /**
   * Avisos que no impiden restaurar: una tabla ausente, un huérfano, un dato
   * con un valor raro o un ajuste que tomará su valor predeterminado.
   */
  advertencias: string[];
  /**
   * Registros que IndexedDB no puede guardar (sin clave válida, repetidos o
   * que no son objetos): impiden restaurar. Un archivo generado por DuoFit
   * nunca los tiene, porque sus registros salen de IndexedDB.
   */
  errores: string[];
  /**
   * true = la suma no coincide o falta: el archivo se modificó o se dañó.
   * Restaurar exige una confirmación reforzada (Req. 6.7).
   *
   * No es un rechazo absoluto a propósito: si ese archivo fuera el único
   * respaldo, negarlo por completo garantizaría perderlo todo (lección de
   * Control de Negocio de Comida).
   */
  exigeConfirmacionReforzada: boolean;
}

export interface RespaldoAnalizado {
  /** El archivo normalizado, con los datos tal como venían. */
  archivo: ArchivoRespaldo;
  /** Los datos ya migrados al esquema actual, listos para escribirse. */
  datos: MapaTablas;
  resumen: ResumenRespaldo;
}

// ---------------------------------------------------------------------------
// Rango de fechas que cubre el respaldo (design.md, 10.1)
// ---------------------------------------------------------------------------

const CAMPOS_FECHA: Partial<Record<NombreTabla, readonly string[]>> = {
  workouts: ['fecha'],
  weighIns: ['fecha'],
  measurements: ['fecha'],
  mealEntries: ['fecha'],
  stages: ['fechaInicio'],
  photos: ['fecha'],
};

export function calcularRangoFechas(
  datos: MapaTablas,
): { from: FechaLocal; to: FechaLocal } | null {
  let desde: FechaLocal | null = null;
  let hasta: FechaLocal | null = null;

  for (const [tabla, campos] of Object.entries(CAMPOS_FECHA)) {
    for (const registro of leerTabla(datos, tabla as NombreTabla)) {
      for (const campo of campos ?? []) {
        const valor = registro[campo];
        if (!esFechaLocal(valor)) continue;
        desde = desde === null ? valor : minima(desde, valor);
        hasta = hasta === null ? valor : maxima(hasta, valor);
      }
    }
  }

  if (desde === null || hasta === null) return null;
  return { from: desde, to: hasta };
}

// ---------------------------------------------------------------------------
// Integridad referencial (advertencias, design.md, 10.1)
// ---------------------------------------------------------------------------

interface ReglaReferencia {
  tabla: NombreTabla;
  destino: NombreTabla;
  /** Devuelve el id referido, o null si este registro no aplica a la regla. */
  referencia: (registro: Readonly<Record<string, unknown>>) => unknown;
  /** true = null significa "sin referencia", que es válido. */
  opcional?: boolean;
}

const campo =
  (nombre: string) =>
  (registro: Readonly<Record<string, unknown>>): unknown =>
    registro[nombre];

/**
 * Referencias que deben existir dentro del mismo archivo. Los ids de la base
 * de alimentos incluida tampoco se revisan: no viven en IndexedDB sino en la app.
 */
const REGLAS_REFERENCIA: readonly ReglaReferencia[] = [
  { tabla: 'routineExercises', destino: 'routines', referencia: campo('routineId') },
  { tabla: 'routineExercises', destino: 'exercises', referencia: campo('exerciseId') },
  // Pasa a null si se borra la rutina (design.md, 7.9)
  { tabla: 'workouts', destino: 'routines', referencia: campo('routineId'), opcional: true },
  { tabla: 'workoutExercises', destino: 'workouts', referencia: campo('workoutId') },
  { tabla: 'workoutExercises', destino: 'exercises', referencia: campo('exerciseId') },
  { tabla: 'workoutSets', destino: 'workoutExercises', referencia: campo('workoutExerciseId') },
  { tabla: 'workoutSets', destino: 'workouts', referencia: campo('workoutId') },
  { tabla: 'weighIns', destino: 'scales', referencia: campo('scaleId'), opcional: true },
  { tabla: 'stages', destino: 'plans', referencia: campo('planId') },
  // Las comidas NO se revisan contra Mis alimentos ni platillos: cada registro
  // lleva su copia de valores y borrar el alimento está permitido (Req. 18.4).
  // Revisarlas haría que todo respaldo posterior avisara de algo normal.
];

function idsDe(datos: MapaTablas, tabla: NombreTabla): Set<string> {
  const ids = new Set<string>();
  for (const registro of leerTabla(datos, tabla)) {
    const id = registro[tabla === 'days' ? 'fecha' : 'id'];
    if (id !== null && id !== undefined) ids.add(String(id));
  }
  return ids;
}

/** Reporta referencias que apuntan a registros inexistentes. No bloquea. */
export function revisarIntegridad(datos: MapaTablas): string[] {
  const problemas: string[] = [];
  const indices = new Map<NombreTabla, Set<string>>();

  for (const regla of REGLAS_REFERENCIA) {
    let validos = indices.get(regla.destino);
    if (validos === undefined) {
      validos = idsDe(datos, regla.destino);
      indices.set(regla.destino, validos);
    }

    let huerfanos = 0;
    for (const registro of leerTabla(datos, regla.tabla)) {
      const valor = regla.referencia(registro);
      if (valor === null || valor === undefined) {
        if (regla.opcional !== true) huerfanos++;
        continue;
      }
      if (!validos.has(String(valor))) huerfanos++;
    }

    if (huerfanos > 0) {
      problemas.push(
        `${ETIQUETAS_TABLA[regla.tabla]}: ${huerfanos} ` +
          `${huerfanos === 1 ? 'registro apunta' : 'registros apuntan'} a ` +
          `${ETIQUETAS_TABLA[regla.destino].toLowerCase()} que no existen en el archivo.`,
      );
    }
  }

  return problemas;
}

// ---------------------------------------------------------------------------
// Validación de registros en memoria (design.md, 10.2, paso 7)
// ---------------------------------------------------------------------------

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor);
}

function esInstante(valor: unknown): boolean {
  return typeof valor === 'string' && valor !== '' && !Number.isNaN(Date.parse(valor));
}

function esNumero(valor: unknown): valor is number {
  return typeof valor === 'number' && Number.isFinite(valor);
}

interface ReglaCampo {
  campo: string;
  descripcion: string;
  valido: (valor: unknown, registro: Readonly<Record<string, unknown>>) => boolean;
}

const fechaValida = (campo: string): ReglaCampo => ({
  campo,
  descripcion: `la fecha (${campo}) no es válida`,
  valido: esFechaLocal,
});

const instanteValido = (campo: string): ReglaCampo => ({
  campo,
  descripcion: `la marca de tiempo (${campo}) no es válida`,
  valido: esInstante,
});

/** Esquema 2: crudo, cocido o sin dato (ausente o null en el esquema 1). */
const estadoValido: ReglaCampo = {
  campo: 'estado',
  descripcion: 'el estado crudo o cocido no es válido',
  valido: (v) => v === undefined || v === null || v === 'crudo' || v === 'cocido',
};

/** Desde la 0.3.5: de dónde salen los valores, o sin dato (lo anterior no lo trae). */
const calidadValida: ReglaCampo = {
  campo: 'calidad',
  descripcion: 'la calidad del dato (etiqueta, referencia, aproximación o estimación) no es válida',
  valido: (v) =>
    v === undefined || v === null || v === 'etiqueta' || v === 'referencia' || v === 'aproximada' || v === 'estimada',
};

/** Desde la 0.3.4: gimnasio, casa o sin dato (lo anterior se lee como gimnasio). */
const modalidadValida: ReglaCampo = {
  campo: 'modalidad',
  descripcion: 'la modalidad (gimnasio o casa) no es válida',
  valido: (v) => v === undefined || v === null || v === 'gimnasio' || v === 'casa',
};

/**
 * Solo lo que usan los índices y los cálculos: tipos y rangos básicos. Solo
 * generan avisos. Corre después de migrar. Los ajustes no van aquí: se
 * normalizan campo por campo (ajustes.ts).
 */
const REGLAS_CAMPOS: Partial<Record<NombreTabla, readonly ReglaCampo[]>> = {
  routines: [modalidadValida],
  workouts: [
    fechaValida('fecha'),
    instanteValido('inicio'),
    {
      campo: 'estado',
      descripcion: 'el estado no es válido',
      valido: (v) => v === 'en_curso' || v === 'terminado',
    },
    modalidadValida,
  ],
  workoutSets: [
    {
      campo: 'pesoKg',
      descripcion: 'el peso no es un número válido',
      valido: (v) => v === null || v === undefined || (esNumero(v) && v >= 0),
    },
  ],
  weighIns: [
    fechaValida('fecha'),
    instanteValido('momento'),
    {
      campo: 'pesoKg',
      descripcion: 'el peso no es un número válido',
      valido: (v) => esNumero(v) && v > 0,
    },
  ],
  measurements: [
    fechaValida('fecha'),
    {
      campo: 'valor',
      descripcion: 'el valor no es un número válido',
      valido: (v) => esNumero(v) && v >= 0,
    },
  ],
  foods: [estadoValido, calidadValida],
  dishes: [
    {
      // Opcional desde la 0.3.1: ausente o null = sin peso final
      campo: 'pesoFinalG',
      descripcion: 'el peso final no es un número válido',
      valido: (v) => v === undefined || v === null || (esNumero(v) && v > 0),
    },
  ],
  mealEntries: [
    fechaValida('fecha'),
    instanteValido('momento'),
    {
      campo: 'valores',
      descripcion: 'las kcal no son un número válido',
      valido: (v) => esObjeto(v) && esNumero(v['kcal']) && v['kcal'] >= 0,
    },
    estadoValido,
    calidadValida,
  ],
  days: [fechaValida('fecha')],
  plans: [fechaValida('fechaInicio')],
  stages: [fechaValida('fechaInicio')],
  maintenance: [fechaValida('desde')],
  fasts: [instanteValido('inicio')],
};

/** La clave primaria de cada tabla, como la declara schema.ts. */
function clavePrimaria(tabla: NombreTabla, registro: Readonly<Record<string, unknown>>): unknown {
  return tabla === 'days' ? registro['fecha'] : registro['id'];
}

/**
 * Lo que IndexedDB acepta como clave en estas tablas: texto o número. Es la
 * misma regla que la base, a propósito: todo lo que salió de IndexedDB se
 * puede volver a escribir, así que un respaldo exportado nunca queda sin
 * poder restaurarse por esta revisión. Los ajustes no se revisan aquí: se
 * normalizan campo por campo al restaurar (ajustes.ts).
 */
function clavePrimariaValida(tabla: NombreTabla, clave: unknown): boolean {
  if (tabla === 'settings') return true;
  return typeof clave === 'string' || (typeof clave === 'number' && Number.isFinite(clave));
}

function contar(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

export interface ValidacionRegistros {
  /**
   * Problemas de estructura que IndexedDB no puede guardar o que harían
   * perder registros (claves inválidas o repetidas): impiden restaurar.
   * Un archivo generado por DuoFit no los tiene, porque sus registros salen
   * de IndexedDB.
   */
  bloqueantes: string[];
  /**
   * Datos con un valor raro (fecha, número o estado): se avisan, pero NO
   * impiden exportar ni restaurar. Bloquear por ellos dejaría sin respaldo a
   * quien tenga un solo registro mal guardado.
   */
  avisos: string[];
}

/**
 * Revisa todos los registros antes de abrir la transacción. Agrupa los
 * problemas para que los mensajes sean cortos.
 */
export function validarRegistros(datos: MapaTablas): ValidacionRegistros {
  const bloqueantes: string[] = [];
  const avisos: string[] = [];
  const errores = bloqueantes;

  for (const tabla of TABLAS) {
    const registros = (datos as unknown as Record<string, unknown>)[tabla];
    if (registros === undefined) continue;
    const etiqueta = ETIQUETAS_TABLA[tabla];
    if (!Array.isArray(registros)) {
      errores.push(`${etiqueta}: se esperaba una lista de registros.`);
      continue;
    }

    let noObjetos = 0;
    let sinClave = 0;
    let repetidos = 0;
    const vistas = new Set<string>();
    const fallasCampo = new Map<string, number>();

    for (const registro of registros as unknown[]) {
      if (!esObjeto(registro)) {
        noObjetos++;
        continue;
      }
      const clave = clavePrimaria(tabla, registro);
      if (!clavePrimariaValida(tabla, clave)) {
        sinClave++;
      } else if (tabla !== 'settings' && vistas.has(`${typeof clave}:${String(clave)}`)) {
        // Con claves repetidas, bulkPut se quedaría solo con el último: se perderían registros.
        repetidos++;
      } else {
        vistas.add(`${typeof clave}:${String(clave)}`);
      }
      for (const regla of REGLAS_CAMPOS[tabla] ?? []) {
        if (!regla.valido(registro[regla.campo], registro)) {
          fallasCampo.set(regla.descripcion, (fallasCampo.get(regla.descripcion) ?? 0) + 1);
        }
      }
    }

    if (noObjetos > 0) {
      errores.push(`${etiqueta}: ${contar(noObjetos, 'elemento no es', 'elementos no son')} un registro.`);
    }
    if (sinClave > 0) {
      errores.push(
        `${etiqueta}: ${contar(sinClave, 'registro no tiene', 'registros no tienen')} un identificador válido.`,
      );
    }
    if (repetidos > 0) {
      errores.push(
        `${etiqueta}: ${contar(repetidos, 'registro repite', 'registros repiten')} el identificador de otro.`,
      );
    }
    if (tabla === 'settings' && registros.length > 1) {
      avisos.push(`${etiqueta}: hay ${registros.length} registros; se usará solo el primero.`);
    }
    for (const [descripcion, n] of fallasCampo) {
      avisos.push(`${etiqueta}: en ${contar(n, 'registro', 'registros')} ${descripcion}.`);
    }
  }

  return { bloqueantes, avisos };
}

// ---------------------------------------------------------------------------
// Serialización y nombres
// ---------------------------------------------------------------------------

/** Con sangría, para que el archivo sea legible si hace falta revisarlo a mano. */
export function serializarRespaldo(archivo: ArchivoRespaldo): string {
  return JSON.stringify(archivo, null, 2);
}

/** Sello local `AAAA-MM-DD-HHmm` para los nombres de archivo (Req. 6.2). */
export function selloLocal(fecha: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return (
    `${fecha.getFullYear()}-${p(fecha.getMonth() + 1)}-${p(fecha.getDate())}` +
    `-${p(fecha.getHours())}${p(fecha.getMinutes())}`
  );
}

/**
 * El nombre dice qué trae el archivo y cómo, para distinguirlos de un
 * vistazo en Archivos del iPhone (Req. 6.2):
 *
 *   duofit-2026-10-21-0930.respaldo.json
 *   duofit-2026-10-21-0930.respaldo-cifrado.json
 *   duofit-2026-10-21-0930.respaldo-con-fotos.zip
 *   duofit-2026-10-21-0930.respaldo-con-fotos-cifrado.json
 *
 * El respaldo con fotos cifrado termina en `.json` porque lo que se guarda es
 * la envoltura cifrada, que es un archivo JSON; el ZIP va cifrado dentro.
 */
export function nombreArchivoRespaldo(fecha = new Date(), cifrado = false, conFotos = false): string {
  const que = conFotos ? 'respaldo-con-fotos' : 'respaldo';
  if (cifrado) return `duofit-${selloLocal(fecha)}.${que}-cifrado.json`;
  return `duofit-${selloLocal(fecha)}.${que}.${conFotos ? 'zip' : 'json'}`;
}

/**
 * Lo que el usuario capturó: todos los registros salvo los ajustes y el
 * catálogo base de ejercicios, que la app crea sola (y vuelve a crear al
 * restaurar). Es el número que se muestra y se compara al respaldar y
 * restaurar; los conteos por tabla siguen siendo exactos.
 */
export function registrosCapturados(datos: Partial<Record<NombreTabla, readonly unknown[]>>): number {
  let suma = 0;
  for (const tabla of TABLAS) {
    if (tabla === 'settings') continue;
    const registros = datos[tabla] ?? [];
    suma +=
      tabla === 'exercises'
        ? registros.filter((r) => !(esObjeto(r) && r['esBase'] === true)).length
        : registros.length;
  }
  return suma;
}

export function tablasVacias(): MapaTablas {
  const vacio = {} as MapaTablas;
  for (const tabla of TABLAS) escribirTabla(vacio, tabla, []);
  return vacio;
}

// ---------------------------------------------------------------------------
// Perfil del respaldo frente al del teléfono (Req. 6.15)
// ---------------------------------------------------------------------------

function leerPerfil(crudo: Record<string, unknown>, datos: MapaTablas): PerfilRespaldo {
  // Primero el de los ajustes, que está cubierto por la suma de verificación.
  const ajustes = leerTabla(datos, 'settings')[0];
  if (ajustes !== undefined && typeof ajustes['perfilId'] === 'string' && ajustes['perfilId'] !== '') {
    return {
      id: ajustes['perfilId'],
      nombre: typeof ajustes['nombre'] === 'string' ? ajustes['nombre'] : null,
    };
  }
  const cabecera = crudo['perfil'];
  if (esObjeto(cabecera)) {
    return {
      id: typeof cabecera['id'] === 'string' && cabecera['id'] !== '' ? cabecera['id'] : null,
      nombre: typeof cabecera['nombre'] === 'string' ? cabecera['nombre'] : null,
    };
  }
  return { id: null, nombre: null };
}

/** "Janeth", o "Sin nombre (per_a48e9c01…)" para distinguir dos perfiles sin nombre. */
export function nombrePerfil(perfil: PerfilRespaldo): string {
  if (perfil.nombre !== null && perfil.nombre.trim() !== '') return perfil.nombre;
  if (perfil.id === null) return 'perfil desconocido';
  return `Sin nombre (${perfil.id.slice(0, 12)}…)`;
}

export type ComparacionPerfil =
  /** El respaldo es de este mismo perfil. */
  | { tipo: 'mismo' }
  /** Teléfono sin datos (nuevo o reinstalado): adopta el perfil del respaldo. */
  | { tipo: 'adopta' }
  /** Perfil distinto y el teléfono tiene datos: confirmación reforzada. */
  | { tipo: 'distinto'; archivo: string; telefono: string };

export function compararPerfil(
  archivo: PerfilRespaldo,
  telefono: PerfilRespaldo,
  telefonoTieneDatos: boolean,
): ComparacionPerfil {
  if (archivo.id !== null && archivo.id === telefono.id) return { tipo: 'mismo' };
  if (!telefonoTieneDatos) return { tipo: 'adopta' };
  return { tipo: 'distinto', archivo: nombrePerfil(archivo), telefono: nombrePerfil(telefono) };
}

// ---------------------------------------------------------------------------
// Análisis previo a restaurar (Req. 6.5 a 6.8)
// ---------------------------------------------------------------------------

/**
 * Valida un archivo de respaldo SIN modificar ningún dato.
 *
 * Orden: de lo más barato a lo más costoso, y de lo que impide continuar a
 * lo que solo advierte.
 *
 *   1. Es JSON válido                                       → rechazo
 *   2. Es de esta app y de un formato conocido              → rechazo
 *   3. La versión de esquema no es posterior a la soportada → rechazo
 *   4. Las tablas son listas                                → rechazo
 *   5. La suma de verificación coincide                     → confirmación reforzada
 *   6. La integridad referencial es correcta                → advertencia
 *   7. Migración en memoria                                 → rechazo si falta un eslabón
 *   8. Ajustes normalizados y validación de registros       → advertencias; solo una
 *      clave inválida o repetida (que IndexedDB no guarda) impide restaurar
 */
export async function analizarRespaldo(texto: string): Promise<RespaldoAnalizado> {
  // 1. JSON válido
  let crudo: unknown;
  try {
    crudo = JSON.parse(texto);
  } catch {
    throw new ErrorRespaldo(
      'El archivo no es un respaldo válido: no se pudo leer su contenido. ' +
        'Verifica que sea el archivo .json que generó DuoFit. No se modificó ningún dato.',
    );
  }

  if (!esObjeto(crudo)) {
    throw new ErrorRespaldo(
      'El archivo no tiene la estructura de un respaldo. No se modificó ningún dato.',
    );
  }

  // 2. App y versión de formato
  if (crudo['app'] !== IDENTIFICADOR_APP) {
    throw new ErrorRespaldo('Este archivo no es un respaldo de DuoFit. No se modificó ningún dato.');
  }

  const formatVersion = crudo['formatVersion'];
  if (typeof formatVersion !== 'number' || !Number.isInteger(formatVersion)) {
    throw new ErrorRespaldo(
      'El archivo no indica una versión de formato válida. No se modificó ningún dato.',
    );
  }
  if (formatVersion === FORMATO_CIFRADO) {
    throw new ErrorRespaldo(
      'Este respaldo está cifrado: escribe su contraseña para abrirlo. No se modificó ningún dato.',
    );
  }
  if (formatVersion > FORMATO_VERSION) throw new ErrorRespaldo(MENSAJE_VERSION_NUEVA);

  // 3. Un esquema posterior se RECHAZA sin tocar nada (Req. 6.6, 6.16)
  const schemaVersion = crudo['schemaVersion'];
  if (typeof schemaVersion !== 'number' || !Number.isInteger(schemaVersion) || schemaVersion < 1) {
    throw new ErrorRespaldo(
      'El archivo no indica una versión de esquema válida. No se modificó ningún dato.',
    );
  }
  if (schemaVersion > VERSION_ESQUEMA) throw new ErrorRespaldo(MENSAJE_VERSION_NUEVA);

  // 4. Tablas
  const data = crudo['data'];
  if (!esObjeto(data)) {
    throw new ErrorRespaldo('El respaldo no contiene datos. No se modificó ningún dato.');
  }

  const advertencias: string[] = [];
  const normalizado = tablasVacias();

  for (const tabla of TABLAS) {
    const registros = data[tabla];
    if (registros === undefined) {
      // Una tabla que todavía no existía en el esquema de ese archivo no falta:
      // la migración la deja vacía y no hay nada que avisar
      const nacioEn = TABLA_DESDE_ESQUEMA[tabla] ?? 1;
      if (nacioEn > schemaVersion) continue;
      advertencias.push(`El respaldo no incluye "${ETIQUETAS_TABLA[tabla]}".`);
      continue;
    }
    if (!Array.isArray(registros)) {
      throw new ErrorRespaldo(
        `"${ETIQUETAS_TABLA[tabla]}" está dañado en el respaldo: se esperaba una lista. ` +
          'No se modificó ningún dato.',
      );
    }
    escribirTabla(normalizado, tabla, registros);
  }

  if (normalizado.settings.length === 0) {
    advertencias.push(
      'El respaldo no trae los ajustes ni el perfil: se conservarán los de este teléfono.',
    );
  }

  // 5. Suma de verificación, sobre `data` TAL COMO SE FIRMÓ: con las tablas que
  // traiga, conocidas o no. Así un respaldo de una versión anterior (sin las
  // tablas nuevas) o con una tabla que una versión posterior quitó se sigue
  // verificando, y una tabla quitada o alterada después de firmar se detecta.
  const checksumEsperado = crudo['checksum'];
  const traeChecksum = typeof checksumEsperado === 'string' && checksumEsperado !== '';
  let checksumValido = false;

  if (traeChecksum) {
    const calculado = await calcularChecksum(data);
    checksumValido = checksumCoincide(checksumEsperado, calculado);
    if (!checksumValido) {
      advertencias.unshift(
        'La suma de verificación NO coincide: el archivo se modificó o se dañó después de ' +
          'crearse. Restaurarlo exige confirmar que entiendes el riesgo.',
      );
    }
  } else {
    advertencias.unshift(
      'El respaldo no trae suma de verificación: no se puede comprobar que esté completo. ' +
        'Restaurarlo exige confirmar que entiendes el riesgo.',
    );
  }

  // 6. Integridad referencial
  advertencias.push(...revisarIntegridad(normalizado));

  // 7. Migración en memoria (lanza si falta un eslabón). Los eslabones ven
  // también las tablas que esta versión ya no conoce, por si una tabla cambió
  // de nombre; después solo se conservan las tablas actuales.
  const paraMigrar = { ...data, ...normalizado } as unknown as MapaTablas;
  const migrados =
    schemaVersion < VERSION_ESQUEMA ? migrarDatos(paraMigrar, schemaVersion) : paraMigrar;
  const datos = tablasVacias();
  for (const tabla of TABLAS) {
    const registros = (migrados as unknown as Record<string, unknown>)[tabla];
    escribirTabla(datos, tabla, Array.isArray(registros) ? registros : []);
  }
  const desconocidas = Object.keys(migrados).filter((c) => !TABLAS.includes(c as NombreTabla));
  if (desconocidas.length > 0) {
    advertencias.push(
      'El respaldo trae información que esta versión no reconoce y se omitirá: ' +
        `${desconocidas.join(', ')}.`,
    );
  }

  // 8. Ajustes normalizados campo por campo y validación de registros
  const ajustesCrudos = datos.settings[0];
  if (esObjeto(ajustesCrudos)) {
    const { ajustes, corregidos } = normalizarAjustes(ajustesCrudos);
    datos.settings = [ajustes];
    if (corregidos.length > 0) {
      advertencias.push(
        'Algunos ajustes del respaldo tienen valores que la app no reconoce y tomarán el ' +
          `valor predeterminado: ${corregidos.join(', ')}.`,
      );
    }
  }
  const validacion = validarRegistros(datos);
  advertencias.push(...validacion.avisos);
  const errores = validacion.bloqueantes;

  const conteos = TABLAS.map((tabla) => ({
    tabla,
    etiqueta: ETIQUETAS_TABLA[tabla],
    registros: normalizado[tabla].length,
  }));
  const rango = calcularRangoFechas(normalizado);
  const perfil = leerPerfil(crudo, normalizado);

  const archivo: ArchivoRespaldo = {
    app: IDENTIFICADOR_APP,
    formatVersion,
    schemaVersion,
    exportedAt: typeof crudo['exportedAt'] === 'string' ? crudo['exportedAt'] : '',
    device: typeof crudo['device'] === 'string' ? crudo['device'] : 'desconocido',
    perfil,
    counts: esObjeto(crudo['counts']) ? (crudo['counts'] as Record<string, number>) : {},
    dateRange: rango,
    checksum: typeof checksumEsperado === 'string' ? checksumEsperado : '',
    data: normalizado,
  };

  return {
    archivo,
    datos,
    resumen: {
      formatVersion,
      schemaVersion,
      exportedAt: archivo.exportedAt,
      device: archivo.device,
      perfil,
      conteos,
      totalRegistros: registrosCapturados(normalizado as unknown as Record<NombreTabla, readonly unknown[]>),
      rangoFechas: rango === null ? null : { desde: rango.from, hasta: rango.to },
      traeChecksum,
      checksumValido,
      requiereMigracion: schemaVersion < VERSION_ESQUEMA,
      advertencias,
      errores,
      exigeConfirmacionReforzada: !checksumValido,
    },
  };
}

// ---------------------------------------------------------------------------
// Verificación del archivo generado, antes de entregarlo (Req. 6.13)
// ---------------------------------------------------------------------------

/**
 * Vuelve a leer el texto generado como si fuera un archivo elegido para
 * restaurar, y exige que la suma coincida y que cada tabla tenga los
 * registros que tenía la base. Lanza si algo no cuadra: el respaldo no se
 * entrega ni se registra.
 *
 * Los avisos de la validación de registros NO bloquean la entrega: un solo
 * dato raro no debe dejar sin respaldo todo lo demás. Se devuelven en el
 * resumen para mostrarlos.
 */
export async function verificarRespaldoGenerado(
  texto: string,
  conteosBase: Readonly<Record<string, number>>,
): Promise<RespaldoAnalizado> {
  const fallo = (detalle: string) =>
    new ErrorRespaldo(
      `No se pudo verificar el respaldo generado (${detalle}). No se entregó ningún archivo; ` +
        'vuelve a intentarlo.',
    );

  let analizado: RespaldoAnalizado;
  try {
    analizado = await analizarRespaldo(texto);
  } catch (error) {
    throw fallo(error instanceof Error ? error.message : 'no se pudo leer');
  }

  const { resumen } = analizado;
  if (!resumen.checksumValido) throw fallo('la suma de verificación no coincide');

  for (const { tabla, etiqueta, registros } of resumen.conteos) {
    // Una tabla que el teléfono no reporta cuenta como 0: ahí no hay nada que
    // perder. Exigir la clave hacía fallar la verificación de un archivo de
    // una versión anterior con un mensaje que se contradecía solo:
    // "Fotos de progreso: 0 en el archivo y 0 en el teléfono".
    const esperados = conteosBase[tabla] ?? 0;
    if (esperados !== registros) {
      throw fallo(`${etiqueta}: ${registros} en el archivo y ${esperados} en el teléfono`);
    }
  }
  return analizado;
}

// ---------------------------------------------------------------------------
// Migración de respaldos antiguos (Req. 6.8, 6.16)
// ---------------------------------------------------------------------------

export type Migracion = (datos: MapaTablas) => MapaTablas;

/**
 * Eslabones de migración: la clave es la versión de origen. Al subir
 * VERSION_ESQUEMA se agrega aquí su eslabón (aunque no transforme nada) y un
 * archivo de muestra en src/db/fixtures (design.md, 10.8).
 */
export const MIGRACIONES: Readonly<Record<number, Migracion>> = {
  // 1 → 2 (Fase 1, Nutrición): solo campos nuevos opcionales. `estado` de
  // Mis alimentos y de las comidas falta y se lee como null; `favoritosBase`
  // de los ajustes lo completa normalizarAjustes con la lista vacía. Los
  // registros del esquema 1 ya son válidos en el 2: no se transforma nada.
  1: (datos) => datos,
  // 2 → 3 (Fase 6, fotos de progreso): la tabla `photos` es nueva, así que un
  // respaldo del esquema 2 simplemente no la trae y se lee como lista vacía.
  // Nada de lo que ya estaba cambia: no se transforma nada.
  2: (datos) => ({ ...datos, photos: Array.isArray(datos.photos) ? datos.photos : [] }),
};

/**
 * Migra los datos de un respaldo desde su versión de esquema hasta la actual,
 * en cadena: 1→2, 2→3… Si falta un eslabón lanza error ANTES de tocar la base.
 *
 * `hasta` y `migraciones` existen para probar la cadena con un esquema de
 * prueba; la app usa los predeterminados.
 */
export function migrarDatos(
  datos: MapaTablas,
  desdeVersion: number,
  hasta: number = VERSION_ESQUEMA,
  migraciones: Readonly<Record<number, Migracion>> = MIGRACIONES,
): MapaTablas {
  let actuales = datos;
  for (let version = desdeVersion; version < hasta; version++) {
    const migracion = migraciones[version];
    if (migracion === undefined) {
      throw new ErrorRespaldo(
        `No existe una migración del esquema ${version} al ${version + 1}. ` +
          'No se modificó ningún dato.',
      );
    }
    actuales = migracion(actuales);
  }
  return actuales;
}
